import React, { useState } from 'react';
import { FaTelegramPlane } from 'react-icons/fa';
import LoginForm from './Login/LoginForm';
import OtpForm from './Login/OTPForm';
import PasswordForm from './Login/PasswordForm';
import RegisterForm from "./Login/RegisterForm";

interface LoginPageProps {
  referalCode: any;
}

const LoginPage: React.FC<LoginPageProps> = ({ referalCode }) => {
  // Current step in the authentication flow
  const [step, setStep] = useState(1);

  // User data that will be collected throughout the process
  const [userData, setUserData] = useState({
    mobileNumber: '',
    countryCode: '91',
    password: '',
    confirmPassword: '',
    username: '',
    otpToken: '',
  });

  // Whether the user exists in the system (determined after OTP verification)
  const [userExists, setUserExists] = useState<boolean | null>(null);

  // Remaining time for verification steps
  const [otpTimeRemaining, setOtpTimeRemaining] = useState(0);
  const [passwordTimeRemaining, setPasswordTimeRemaining] = useState(0);

  // Update user data through the flow
  const updateUserData = (data: Partial<typeof userData>) => {
    setUserData(prev => ({ ...prev, ...data }));
  };

  // Advance to next step in the flow
  const goToNextStep = () => {
    setStep(prevStep => prevStep + 1);
  };

  // Go back to previous step
  const goToPreviousStep = () => {
    setStep(prevStep => Math.max(1, prevStep - 1));
  };

  // Set the verification times
  const startOtpTimer = () => {
    setOtpTimeRemaining(90); // 90 seconds for OTP verification
  };

  const startPasswordTimer = () => {
    setPasswordTimeRemaining(300); // 5 minutes (300 seconds) for password entry
  };

  // Handle OTP verification result
  const handleOtpVerified = (exists: boolean, token: string) => {
    setUserExists(exists);
    updateUserData({ otpToken: token });
    startPasswordTimer();
    goToNextStep();
  };

  // Go directly to login step
  const goToLoginStep = () => {
    setStep(1);
  };

  return (
    <div className="w-full max-w-md mx-auto p-6 bg-zinc-900 text-white rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-white">
          {step === 1 && 'Sign In'}
          {step === 2 && 'Verify OTP'}
          {step === 3 && 'Complete Verification'}
        </h2>
        <p className="text-sm text-zinc-400">
          {step === 1 && 'Please enter your mobile number.'}
          {step === 2 && 'Enter the OTP sent to your phone.'}
          {step === 3 && 'Complete your account information.'}
        </p>
      </div>

      <div className="p-4 rounded-lg bg-zinc-800 border border-zinc-700 mb-4">
        <a
          href="https://t.me/strike46_bot"
          target="_blank"
          rel="noopener noreferrer"
          className="no-underline"
        >
          <div className="flex items-center gap-2 p-3 bg-zinc-700 rounded-md hover:bg-zinc-600 cursor-pointer transition-colors border border-zinc-600">
            <FaTelegramPlane className="text-blue-400 w-5 h-5" />
            <span className="text-sm text-blue-400">
              Try out our site and Play on Telegram
            </span>
          </div>
        </a>
      </div>

      <div className="p-4 rounded-lg bg-zinc-800 border border-zinc-700">
        {step === 1 && (
          <LoginForm
            userData={userData}
            updateUserData={updateUserData}
            goToNextStep={goToNextStep}
            startOtpTimer={startOtpTimer}
          />
        )}

        {step === 2 && (
          <OtpForm
            userData={userData}
            timeRemaining={otpTimeRemaining}
            setTimeRemaining={setOtpTimeRemaining}
            onOtpVerified={handleOtpVerified}
            goToPreviousStep={goToPreviousStep}
            updateUserData={updateUserData}
          />
        )}

        {step === 3 && userExists === true && (
          <PasswordForm
            userData={userData}
            updateUserData={updateUserData}
            timeRemaining={passwordTimeRemaining}
            setTimeRemaining={setPasswordTimeRemaining}
            goToPreviousStep={goToPreviousStep}
            goToLoginStep={goToLoginStep}
          />
        )}

        {step === 3 && userExists === false && (
          <RegisterForm
            userData={userData}
            updateUserData={updateUserData}
            timeRemaining={passwordTimeRemaining}
            setTimeRemaining={setPasswordTimeRemaining}
            goToPreviousStep={goToPreviousStep}
            referal={referalCode}
            goToLoginStep={goToLoginStep}
          />
        )}
      </div>
    </div>
  );
};

export default LoginPage;